<template>
  <div class="is-relative">
    <is-loading :if="isLoading" :has-padding="false">
      <u-field :label="label" expanded>
        <selector
          v-show="smartDomainOptions.length > 1"
          :items="smartDomainOptions"
          v-model="selectedOption"
          native-value-path="value"
          :allow-unselect="false"
          class="has-margin-bottom-50"
        />
      </u-field>

      <!-- DAC Controls -->
      <template v-if="showDacControls">
        <!-- Register / Transfer Summary -->
        <template v-if="isReadonly">
          <u-message :type="message.type">
            <i18n :path="message.path">
              <template #domain>
                <strong>{{ formField }}</strong>
              </template>
            </i18n>
            <template #action>
              <u-link :disabled="isDisabled" @click="toggleReadonly(false)">
                <i18n path="_modules.web_hosting.domains.change_domain" />
              </u-link>
            </template>
          </u-message>
        </template>

        <template v-else>
          <upm-dac
            :auto-focus="true"
            :key="selectedOption"
            :auth-token="authToken"
            :account-id="accountId"
            :basket-id="basketId"
            :brand-id="brandId"
            :coupons="coupons"
            :currency-id="currencyId"
            :on-add-to-basket="addToBasket"
            class="is-paddingless"
            @unauthorised="$store.dispatch('auth/refreshAccessToken')"
          >
            <p class="is-size-8 has-text-grey has-margin-top-100">
              *
              <i18n tag="em" :path="pricesMessage" />
              <i18n
                tag="em"
                path="_sentence.product.renewal_prices_exclude_discounts_msg"
              />
            </p>
          </upm-dac>
          <u-field v-bind="{ rules, vid, vname: vname.toLocaleLowerCase() }">
            <input v-model="formField" type="hidden" />
          </u-field>
        </template>
      </template>

      <!-- Basket Domains -->
      <template v-else-if="showBasketDomainSelect">
        <b-select
          v-model="formField"
          :disabled="isDisabled"
          :placeholder="$t('_modules.web_hosting.domains.select_a_domain')"
          expanded
        >
          <template v-if="basketDomains.length">
            <optgroup
              :label="
                $t('_modules.web_hosting.domains.select_a_domain_from_basket')
              "
            >
              <template v-for="domain in basketDomains">
                <option :key="domain" :value="domain">{{ domain }}</option>
              </template>
            </optgroup>
          </template>
        </b-select>
      </template>

      <!-- Existing Domains (incl. Account Domains) -->
      <template v-else>
        <u-field
          v-bind="{
            vid,
            vname: vname.toLocaleLowerCase(),
            key: `provision-field-${field.id}`
          }"
        >
          <b-autocomplete
            v-focus="!formField?.length"
            v-model="formField"
            field="domain_name"
            :placeholder="
              $t('_modules.web_hosting.domains.existing_placeholder')
            "
            :open-on-focus="true"
            :data="accountDomainsFiltered"
            :clearable="true"
          />
        </u-field>
      </template>
    </is-loading>
  </div>
</template>

<script lang="ts">
import BrandCurrencyId from "@/mixins/brandCurrencyId";
import Context from "@/mixins/context";
import HasValidation from "@/mixins/HasValidation";
import _ from "@/boot/lodash";
import type { IAccount } from "@/models/accounts";
import type { IBasket } from "@/models/baskets";
import type { IBlueprintField } from "@/models/provisioning";
import type { IBrand } from "@/models/brands";
import type { ICurrency } from "@/models/constants";
import type { PropType } from "vue";
import { BasketTypes } from "@/data/constants";
import { DataModules } from "@/store/modules/data/modules";
import { UpmDac } from "@upmind-widgets/dac";
import { UpmindObjectTypes } from "@/data/enums/objects";
import { defineComponent } from "vue";

enum SmartOptions {
  REGISTER = "register_new_domain",
  TRANSFER = "transfer_old_domain",
  BASKET = "use_basket_domain",
  EXISTING = "use_existing_domain"
}

export default defineComponent({
  name: "SmartDomainField",
  components: {
    "upm-dac": UpmDac
  },
  mixins: [HasValidation, Context, BrandCurrencyId],
  props: {
    value: {
      type: null as unknown as PropType<string | null>,
      required: true
    },
    isDisabled: { type: Boolean, required: true },
    field: { type: Object as PropType<IBlueprintField>, required: true },
    accountId: { type: String as PropType<IAccount["id"]> },
    basketId: { type: String as PropType<IBasket["id"]>, required: true },
    brandId: { type: String as PropType<IBrand["id"]>, required: true },
    coupons: { type: Array as PropType<string[]>, default: () => [] },
    currencyId: {
      type: String as PropType<ICurrency["id"]>,
      default: vm => vm.brandCurrencyId as ICurrency["id"]
    },
    pricesMessage: { type: String, required: true },
    label: { type: String, default: "" },
    vname: { type: String, required: true },
    vid: { type: String, required: true },
    rules: { type: [Object, String], default: "" }
  },
  data: () => ({
    contextualDomains: null as any[] | null,
    dacIsAvailable: false,
    isKnownRegistration: false,
    isKnownTransfer: false,
    isLoading: true,
    isReadonly: false,
    newDacProduct: null,
    selectedOption: null as SmartOptions | null
  }),
  computed: {
    smartDomainOptions() {
      return _.filter(
        [
          {
            leftLabel: this.$t(
              "_modules.web_hosting.domains.register_new_domain"
            ),
            value: SmartOptions.REGISTER,
            if: () => this.dacIsAvailable
          },
          {
            leftLabel: this.$t(
              "_modules.web_hosting.domains.transfer_old_domain"
            ),
            value: SmartOptions.TRANSFER,
            if: () => this.dacIsAvailable
          },
          {
            leftLabel: this.$t(
              "_modules.web_hosting.domains.use_existing_domain"
            ),
            value: SmartOptions.EXISTING,
            if: () => true
          },
          {
            leftLabel: this.$t(
              "_modules.web_hosting.domains.use_basket_domain"
            ),
            value: SmartOptions.BASKET,
            if: () => this.basketDomains.length
          }
        ],
        option => option.if()
      );
    },
    showDacControls() {
      return (
        [SmartOptions.REGISTER, SmartOptions.TRANSFER].includes(
          this.selectedOption as SmartOptions
        ) && this.dacIsAvailable
      );
    },
    showBasketDomainSelect() {
      return [SmartOptions.BASKET].includes(
        this.selectedOption as SmartOptions
      );
    },
    formField: {
      get() {
        return this.value;
      },
      set(value: string) {
        this.$emit("input", value);
      }
    } as unknown as string | null,
    authToken() {
      return _.get(
        this.$store,
        `state.auth.${this.context}.token.access_token`,
        ""
      );
    },
    basketDomains() {
      return this.filterDomainsByType(
        this.contextualDomains,
        UpmindObjectTypes.INVOICE_PRODUCT
      );
    },
    accountDomains() {
      return this.filterDomainsByType(
        this.contextualDomains,
        UpmindObjectTypes.CONTRACTS_PRODUCT
      );
    },
    accountDomainsFiltered() {
      if (!this.formField?.length) return this.accountDomains;
      return this.accountDomains.filter((domain: string) => {
        const matches = domain.match(new RegExp(this.formField, "gi"));
        return matches?.length;
      });
    },
    message() {
      return {
        type: this.isKnownRegistration ? "is-success" : "is-info",
        path: this.isKnownRegistration
          ? "_modules.web_hosting.domains.domain_to_register"
          : this.isKnownTransfer
          ? "_modules.web_hosting.domains.domain_to_transfer"
          : "_modules.web_hosting.domains.selected_domain"
      };
    }
  },
  watch: {
    selectedOption: { handler: "onSelectedOptionChange" }
  },
  async created() {
    try {
      this.isLoading = true;
      await Promise.all([
        this.getDacAvailability(),
        this.getContextualDomains()
      ]);
      this.selectInitialOption();
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    async getDacAvailability() {
      this.dacIsAvailable = await this.$store.dispatch(
        `data/${DataModules.HOST_MODULE}/getDacAvailability`,
        {
          params: {
            no_cache: true,
            ...(this.basketId !== BasketTypes.NEW
              ? { order_id: this.basketId }
              : { currency_id: this.currencyId })
          }
        }
      );
    },
    async addToBasket({ _product, sld = "" }) {
      this.$emit("input", `${sld}${_product.tld}`);

      _product.prices = _.orderBy(_product.prices, ["billing_cycle_months"]);
      _product.options = _.map(_product.options, option => {
        option.prices = _.orderBy(option.prices, ["billing_cycle_months"]);
        return option;
      });

      this.newDacProduct = _product;
      this.isReadonly = true;
      this.isKnownRegistration = _product.domain_available;
      this.isKnownTransfer = !_product.domain_available;

      this.selectedOption = this.isKnownRegistration
        ? SmartOptions.REGISTER
        : SmartOptions.TRANSFER;

      this.$bus.$emit("basketUpdateCombinedProduct", {
        product: _product,
        sld,
        fieldName: this.field.name
      });
    },
    toggleReadonly(value: boolean) {
      this.isReadonly = value;
      if (!this.isReadonly) {
        this.$emit("input", null);
        this.newDacProduct = null;
        this.$bus.$emit("basketUpdateCombinedProduct", null);
        this.isKnownRegistration = false;
        this.isKnownTransfer = false;
      }
    },
    /**
     * Here we get contextual domain suggestions based on the basket ID
     * and related client session (if applicable). For example, if someone has
     * previously added a domain to their basket and then proceeds to add a hosting
     * product, we can intelligently suggest the in-basket domain is used as the
     * 'account domain' for the hosting product. We can also suggest previously
     * purchased domains for logged in clients.
     */
    async getContextualDomains() {
      try {
        this.contextualDomains = await this.$store.dispatch(
          `data/${DataModules.HOST_MODULE}/getClientDomains`,
          {
            vm: this,
            params: {
              ...(this.basketId !== BasketTypes.NEW
                ? { basket_id: this.basketId }
                : {})
            },
            storeData: false
          }
        );
      } catch {
        // Silently catch error
      }
    },
    filterDomainsByType(
      domains: any[] = [],
      type: UpmindObjectTypes
    ): string[] {
      const filtered = _.filter(domains, d => d.order_product_type === type);
      return _.map(filtered, "domain_name").sort();
    },
    async selectInitialOption() {
      // If no set value but basket domains exist » set value & switch option
      if (!this.formField?.length && this.basketDomains.length) {
        this.formField = _.first(this.basketDomains) as string;
        return (this.selectedOption = SmartOptions.BASKET);
      }

      // If value is found within basket domains, switch to basket option
      if (this.basketDomains.includes(this.formField)) {
        return (this.selectedOption = SmartOptions.BASKET);
      }

      // If value is found within account domains, switch to existing option
      if (this.accountDomains.includes(this.formField)) {
        return (this.selectedOption = SmartOptions.EXISTING);
      }

      // If value exists but NOT within contextual domains, switch to existing option
      if (this.formField?.length) {
        return (this.selectedOption = SmartOptions.EXISTING);
      }

      // Fallback to register option
      return (this.selectedOption = SmartOptions.REGISTER);
    },
    onSelectedOptionChange(newOption: SmartOptions, oldOption: SmartOptions) {
      if (!oldOption) return;

      if (newOption === SmartOptions.BASKET)
        return this.doOptionChangeReset(_.first(this.basketDomains));

      if (newOption === SmartOptions.EXISTING)
        return this.doOptionChangeReset();

      if (newOption === SmartOptions.TRANSFER && this.isKnownTransfer) return;
      if (newOption === SmartOptions.REGISTER && this.isKnownRegistration)
        return;

      if ([SmartOptions.REGISTER, SmartOptions.TRANSFER].includes(newOption))
        return this.doOptionChangeReset();
    },
    doOptionChangeReset(value = "") {
      this.formField = value;
      this.isKnownRegistration = false;
      this.isKnownTransfer = false;
      this.isReadonly = false;
    }
  }
});
</script>
